import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from '../api/client';
import { API } from '../api/config';
import { CreditCard, Calendar, CheckCircle } from 'lucide-react';

interface Subscription {
  id: string;
  status: string;
  current_period_start: string;
  current_period_end: string;
  plan: {
    name: string;
    price: number;
    description: string;
  };
}

const Subscriptions: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);

  const fetchSubscription = useCallback(async () => {
    if (!user) return;
    const token = localStorage.getItem('access_token');
    const headers = { Authorization: `Bearer ${token}` };
    try {
      const response = await axios.get(API.getUserSubscription(user.id), { headers });
      setSubscription(response.data);
    } catch (err) {
      console.error('Failed to load subscription:', err);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  const handleCancel = async () => {
    if (!user || !window.confirm('Are you sure you want to cancel your subscription?')) return;
    setCancelling(true);
    const token = localStorage.getItem('access_token');
    const headers = { Authorization: `Bearer ${token}` };
    try {
      await axios.post(API.cancelSubscription(user.id), {}, { headers });
      await fetchSubscription();
    } catch (err) {
      console.error('Failed to cancel subscription:', err);
      alert('Failed to cancel subscription. Please try again.');
    } finally {
      setCancelling(false);
    }
  };

  if (loading) return <div>Loading...</div>;

  return (
    <div>
      <h1 className="page-title">Subscription Status</h1>

      {subscription ? (
        <div className="section-card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '32px' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--accent-primary)', marginBottom: '8px' }}>
                <CreditCard size={20} />
                <span style={{ fontWeight: 600, fontSize: '14px', textTransform: 'uppercase' }}>Current Plan</span>
              </div>
              <h2 style={{ fontSize: '32px', fontWeight: 700 }}>{subscription.plan.name}</h2>
              <p style={{ color: 'var(--text-secondary)', marginTop: '4px' }}>{subscription.plan.description}</p>
            </div>
            <div className={`status-pill ${subscription.status === 'active' ? 'status-active' : 'status-cancelled'}`}>
              {subscription.status.toUpperCase()}
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '32px', borderTop: '1px solid var(--sidebar-border)', paddingTop: '32px' }}>
            <div>
              <div className="form-label">Price</div>
              <div style={{ fontSize: '20px', fontWeight: 600 }}>${subscription.plan.price}/mo</div>
            </div>
            <div>
              <div className="form-label">Next Billing</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '20px', fontWeight: 600 }}>
                <Calendar size={18} className="text-secondary" />
                {new Date(subscription.current_period_end).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
              </div>
            </div>
          </div>

          <div style={{ marginTop: '40px', display: 'flex', gap: '12px' }}>
            {subscription.status === 'active' && (
              <button 
                className="btn btn-danger" 
                onClick={handleCancel}
                disabled={cancelling}
              >
                {cancelling ? 'Cancelling...' : 'Cancel Subscription'}
              </button>
            )}
            <button className="btn btn-outline" onClick={() => navigate('/plans')}>Change Plan</button>
          </div>
        </div>
      ) : (
        <div className="section-card" style={{ textAlign: 'center', padding: '60px' }}>
          <div style={{ marginBottom: '24px' }}>
            <CreditCard size={48} style={{ color: 'var(--text-secondary)', opacity: 0.3 }} />
          </div>
          <h2 className="card-title">No Active Subscription</h2>
          <p style={{ color: 'var(--text-secondary)', marginBottom: '32px' }}>
            You don't have an active subscription plan. Choose a plan to get started.
          </p>
          <button className="btn btn-primary" onClick={() => navigate('/plans')}>View Plans</button>
        </div>
      )}

      <div className="section-card">
        <h3 className="card-title">Plan Benefits</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '20px' }}>
          {[
            'Unlimited Tax Refund Claims',
            'Priority Email Support',
            'Dedicated Account Manager',
            'Early Access to New Features'
          ].map((benefit) => (
            <div key={benefit} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <CheckCircle size={18} style={{ color: 'var(--success-color)' }} />
              <span style={{ fontSize: '14px' }}>{benefit}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Subscriptions;
